import { Refresh } from "@browser/Browser";
import * as rt from "@common/AppRT";
import { boolCompare, isNullOrWhitespace, numCompare } from "@common/Common";
import { CriterionDetailsDialogViewModel } from "@Criteria/Hierarchies/Components/CriterionDetailsDialog";
import CriterionDetailsDialog from "@Criteria/Hierarchies/Components/CriterionDetailsDialog.vue";
import { IEvaluationCommand } from "@Evaluation/cqrs";
import { SavePanelistVoteCommandV1, SavePanelistVoteCommentCommandV1 } from "@Evaluation/CriteriaSurveys/Domain/commands-core";
import CriteriaSurveyCommands from '@Evaluation/CriteriaSurveys/Domain/commands-vue-component';
import CriteriaSurveysQueries from '@Evaluation/CriteriaSurveys/Domain/queries-vue-component';
import { CriteriaVoteEntry, MeetingQuestionReviewReadModel, PanelistMeetingPageReadModel, VoteReadModel } from "@Evaluation/CriteriaSurveys/Domain/readmodels";
import Component, { mixins } from 'vue-class-component';


@Component({
    components: { CriterionDetailsDialog }
})
export default class PanelistMeetingPage extends mixins(CriteriaSurveysQueries, CriteriaSurveyCommands)
{
    get workspaceId() { return Number.parseInt(this.$route.params.workspaceId); }
    get surveyId(): number { return Number.parseInt(this.$route.params.surveyId); }
    get projectUserId(): number { return Number.parseInt(this.$route.params.projectUserId); }

    handler: any = null;
    SavingCommentHandle: any = null;
    connecting: boolean = true;
    initialized: boolean = false;
    meetingStarted: boolean = false;
    browserActiveTab: string = "votes";
    ScaleView1: number[] = [9, 8, 7, 6, 5, 4, 3, 2, 1, 1.0 / 2.0, 1.0 / 3.0, 1.0 / 4.0, 1.0 / 5.0, 1.0 / 6.0, 1.0 / 7.0, 1.0 / 8.0, 1.0 / 9.0].sort((a, b) => numCompare(a, b));
    criterionDetailsDialog: CriterionDetailsDialogViewModel = new CriterionDetailsDialogViewModel();

    rm: PanelistMeetingPageReadModel = null;
    Value: number = null;
    Comment: string = null;

    get question() { return this.rm.Question; }
    get dashboardUrl() { return `/#/weighting/dashboard/${this.workspaceId}/${this.surveyId}/${this.projectUserId}`; }
    get contextName() { return this.question.ContextCriterionName; }
    get firstCriterionName() { return this.question.FirstCriterionName; }
    get secondCriterionName() { return this.question.SecondCriterionName; }
    get votesEntries(): CriteriaVoteEntry[] { return this.question?.VotesEntries ?? null; }

    get viewTemplate(): string
    {
        if (this.connecting === true) { return "connecting"; }
        else if (this.connecting === false && this.initialized === true && this.meetingStarted === false) { return "notStarted"; }
        else { return "default"; }
    }

    get EqualitySelected(): boolean { return this.Value != null && this.Value === 1.0; }
    get FirstSelected(): boolean { return this.Value != null && this.Value > 1.0; }
    get SecondSelected(): boolean { return this.Value != null && this.Value < 1.0; }
    get FirstBtnLabel(): string { return this.FirstSelected ? `${this.GetLabel(this.IntegerValue)} (${this.IntegerValue})` : "More important"; }
    get SecondBtnLabel(): string { return this.SecondSelected ? `${this.GetLabel(this.IntegerValue)} (${this.IntegerValue})` : "More important"; }
    get IntegerValue(): number | null
    {
        if (!this.Value) return null;
        const value = this.Value < 1 ? 1 / this.Value : this.Value;
        return Math.round(value * 100) / 100;
    }

    get Average(): number 
    {
        const votesEntries = this.votesEntries.filter(a => a.Value != null && a.VoteStrength > 0);

        if (votesEntries.length < 1)
        {
            return null;
        }

        const sum = votesEntries.map(a => a.VoteStrength)
            .reduce((sum, current) => sum + current, 0);

        let avg = 1.0;

        for (const voteEntry of votesEntries)
        {
            avg *= Math.pow(voteEntry.Value, voteEntry.VoteStrength / sum);
        }

        return avg;
    }

    get VotesEntriesView(): CriteriaVoteEntry[] 
    {
        return this.votesEntries?.sort((a, b) => boolCompare(a.SurveyId != null, b.SurveyId != null)) ?? [];
    }

    get Comments(): CriteriaVoteEntry[]  
    {
        return this.votesEntries.filter(a => isNullOrWhitespace(a.Comment) === false);
    }

    get ScaleView(): number[]
    {
        const average = this.Average;

        const scale = [...this.ScaleView1];

        if (average != null)
        {
            if (scale.some(a => Math.round(100 * a) !== Math.round(100 * average)))
            {
                scale.push(average);
            }
        }

        return scale.sort((a, b) => numCompare(a, b)).reverse();
    }

    get LeftScaleValuesCount(): number { return this.ScaleView.filter(a => a > 1).length; }

    get RightScaleValuesCount(): number { return this.ScaleView.filter(a => a < 1).length; }

    get AverageDescription(): string 
    {
        const avg = this.Average;

        if (avg == null)
        {
            return "N/A";
        }

        if (avg < 1)
        {
            return (1 / avg).toFixedIfNeeded(2);
        }

        return avg.toFixedIfNeeded(2);
    }

    get HistogramValues()
    {
        const voteValues = this.votesEntries.filter(a => a.Value != null).map(a => a.Value);
        const uniqueValues = new Set([...this.ScaleView1, ...voteValues]);
        const orderedValues = Array.from(uniqueValues).sort((a, b) => b - a);

        let maxCount = 0;

        for (const value of orderedValues)
        {
            const count = voteValues.filter(a => a === value).length;
            maxCount = Math.max(maxCount, count);
        }

        return orderedValues.map(value =>
        {
            const count = voteValues.filter(a => a === value).length;
            const percentOfMax = Math.round(count / maxCount * 100);

            return {
                Value: value,
                Count: count,
                PercentOfMax: percentOfMax
            };
        });
    }


    GetLabel(value: number): string
    {
        switch (value)
        {
            case 2.0: return "Weakly";
            case 3.0: return "Moderately";
            case 4.0: return "Moderately +";
            case 5.0: return "Strongly";
            case 6.0: return "Strongly +";
            case 7.0: return "Very Strongly";
            case 8.0: return "Very Strongly +";
            case 9.0: return "Extremely";
            default: return "";
        }
    }


    FormatValue(value: number): number
    {
        return value < 1 ? 1 / value : value;
    }

    GetColor(value: number): string
    {
        const roundedValue = Math.round(100 * value) / 100;

        if (roundedValue === 1)
        {
            return "bg-color-green2";
        }

        if (roundedValue > 1)
        {
            return "bg-color-blue2";
        }

        return "bg-color-red2";
    }

    GetScaleItemType(scaleValue: number, voteValue: number): string
    {
        //no vote
        if (voteValue == null)
        {
            return "Empty";
        }

        //vote on opposite side
        if (scaleValue < 1.0 && voteValue > 1.0)
        {
            return "Empty";
        }

        //vote on opposite side
        if (scaleValue > 1.0 && voteValue < 1.0)
        {
            return "Empty";
        }

        let x = null;

        if (scaleValue <= 1.0 && voteValue <= 1.0)
        {
            x = (1.0 / scaleValue) - (1.0 / voteValue);
        }
        else if (scaleValue >= 1.0 && voteValue >= 1.0)
        {
            x = scaleValue - voteValue;
        }

        x = Math.round(100 * x) / 100;

        if (x === 0.0)
        {
            return "Filled";
        }

        if (x >= 1.0 || x <= -1.0)
        {
            return "Empty";
        }

        if (x > -1.0 && x < 0.0)
        {
            return "Hidden";
        }

        if (x > 0.0 && x < 1.0)
        {
            //alert(x);
            return "DoubleFilled";
        }

        return null;
    }

    GetCssClass(scaleValue: number, voteValue: number): string
    {
        const scaleItemType = this.GetScaleItemType(scaleValue, voteValue);

        switch (scaleItemType)
        {
            case "Empty":
                return "EmptyScaleItem";
            case "Filled":
                return `FilledScaleItem ${this.GetColor(voteValue)}`;
            case "DoubleFilled":
                return `DoubleFilledScaleItem ${this.GetColor(voteValue)}`;
            case "Hidden":
                return "HiddenScaleItem";
            default:
                return "";
        }
    }

    GetScaleItemText(scaleValue: number, voteValue: number): string
    {
        const scaleItemType = this.GetScaleItemType(scaleValue, voteValue);
        return scaleItemType !== "DoubleFilled" ? this.FormatValue(scaleValue).toLocaleString() : this.FormatValue(voteValue).toFixedIfNeeded(2);
    }


    showContextCriterionDetails()
    {
        this.criterionDetailsDialog.show(this.contextName, this.question.ContextCriterionPath, this.question.ContextCriterionDescription);
    }

    showFirstCriterionDetails()
    {
        this.criterionDetailsDialog.show(this.firstCriterionName, this.question.FirstCriterionPath, this.question.FirstCriterionDescription);
    }

    showSecondCriterionDetails()
    {
        this.criterionDetailsDialog.show(this.secondCriterionName, this.question.SecondCriterionPath, this.question.SecondCriterionDescription);
    }


    SetQuestion(question: MeetingQuestionReviewReadModel)
    {
        this.rm.Question = question;

        const self = question.VotesEntries?.find(a => a.ProjectUserId === this.projectUserId) ?? null;

        if (self != null)
        {
            self.VotesSourceName = "Me";
        }

        this.loadVote();
    }

    SetQuestionVotes(question: MeetingQuestionReviewReadModel)
    {
        if (this.question.Id === question.Id)
        {
            const self = question.VotesEntries?.find(a => a.ProjectUserId === this.projectUserId) ?? null;

            if (self != null)
            {
                self.VotesSourceName = "Me";
            }

            this.question.VotesEntries = question.VotesEntries;
        }
    }

    SetVotesBrowserEntry(entry: CriteriaVoteEntry)
    {
        if (this.question.Id === entry.QuestionId)
        {
            const current = this.votesEntries.find(a => a.ProjectUserId === entry.ProjectUserId);
            current.Value = entry.Value;
            current.Comment = entry.Comment;
        }
    }


    loadVote()
    {
        this.runProjectUserVoteQueryV1(this.workspaceId, this.projectUserId, this.question.Id);
    }

    handleSavePanelistVoteCommentCommand(comment: string)
    {
        this.Comment = comment;

        if (this.SavingCommentHandle == null)
        {
            this.SavingCommentHandle = setTimeout(() =>
            {
                this.invokeSavePanelistVoteCommentCommandV1(this.workspaceId, this.surveyId, this.projectUserId, this.question.Id, this.Comment);
                this.SavingCommentHandle = null;
            }, 1000);
        }
    }

    handleSavePanelistVoteCommand(value: number)
    {
        this.Value = value;
        this.invokeSavePanelistVoteCommandV1(this.workspaceId, this.surveyId, this.projectUserId, this.question.Id, this.Value, this.Comment);
    }


    mounted()
    {
        this.handler = rt.subscribeModule("CriteriaSurveys.MeetingPanelist",
            {
                MeetingStopped: (data: any) =>
                {
                    if (this != null)
                    {
                        Refresh();
                    }
                },
                MeetingStarted: (data: MeetingQuestionReviewReadModel) =>
                {
                    if (this.meetingStarted === false)
                    {
                        this.connecting = false;
                        this.meetingStarted = true;
                        this.SetQuestion(data);
                    }
                },
                SetQuestion: (data: MeetingQuestionReviewReadModel) =>
                {
                    if (this != null)
                    {
                        this.SetQuestion(data);
                    }
                },
                SetQuestionVotes: (data: MeetingQuestionReviewReadModel) =>
                {
                    if (this != null)
                    {
                        this.SetQuestionVotes(data);
                    }
                },
                SetVoteEntry: (data: CriteriaVoteEntry) =>
                {
                    if (this != null)
                    {
                        this.SetVotesBrowserEntry(data);
                    }
                },
            });

        this.runPanelistMeetingPageQueryV1(this.workspaceId, this.surveyId, this.projectUserId);
    }

    onSetModel(model: any, modelName: string) 
    {
        if (modelName == "PanelistMeetingPageReadModel")
        {
            this.rm = model as PanelistMeetingPageReadModel;

            const handle = setTimeout(() =>
            {
                this.connecting = false;
                clearTimeout(handle);
            }, 10000);

            this.requestMeetingStatus();
            this.initialized = true;
        }
        else if (modelName == "VoteReadModel")
        {
            const vote = model as VoteReadModel;

            if (this.question.Id === vote.QuestionId)
            {
                this.Value = vote.Value;
                this.Comment = vote.Comment;
            }
        }
    }

    onCommandSucceed(cmd: IEvaluationCommand, correlationId: string, result: any)
    {
        if (SavePanelistVoteCommandV1.matchType(cmd))
        {
            const cmd1 = cmd as SavePanelistVoteCommandV1;
            this.notifyVoteValueChanged(cmd1.QuestionId, cmd1.VoteValue);
        }
        else if (SavePanelistVoteCommentCommandV1.matchType(cmd))
        {
            const cmd1 = cmd as SavePanelistVoteCommentCommandV1;
            this.notifyVoteCommentChanged(cmd1.QuestionId, cmd1.Comment);
        }
    }


    requestMeetingStatus()
    {
        rt.send("CriteriaSurveys.MeetingPanelist", "RequestMeetingStatus",
            {
                WorkspaceId: this.workspaceId,
                SurveyId: this.surveyId,
                ProjectUserId: this.projectUserId,
            });
    }

    notifyVoteValueChanged(questionId: string, value: number | null)
    {
        rt.send("CriteriaSurveys.MeetingPanelist", "NotifyVoteValueChanged",
            {
                WorkspaceId: this.workspaceId,
                SurveyId: this.surveyId,
                ProjectUserId: this.projectUserId,
                QuestionId: questionId,
                VoteValue: value
            });
    }

    notifyVoteCommentChanged(questionId: string, comment: string)
    {
        rt.send("CriteriaSurveys.MeetingPanelist", "NotifyVoteCommentChanged",
            {
                WorkspaceId: this.workspaceId,
                SurveyId: this.surveyId,
                ProjectUserId: this.projectUserId,
                QuestionId: questionId,
                Comment: comment
            });
    }

    beforeDestroy()
    {
        if (this.handler)
        {
            rt.unsubscribe(this.handler);
        }
    }
}